'use client'

import * as React from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { Bell, BookmarkPlus, LogIn, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  PillButton,
  REmptyState,
  RToastProvider,
  useRToast,
} from '@/components/roostr'
import { SavedSearchCard, cadenceOf, type SearchCadence } from '@/components/searches'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuth } from '@/components/auth'
import { api, ApiError } from '@/lib/api'
import { useSearchStore } from '@/lib/store'
import { useSubscription } from '@/lib/subscription'
import { UpgradePrompt } from '@/components/subscription'
import { FILTER_TAGS, STATIC_FILTER_TAGS } from '@/lib/constants'
import { useI18n } from '@/lib/i18n'
import type { RentalFilters, SavedSearch, SavedSearchCreate } from '@/lib/types'

// Saved searches page — Roostr Web redesign Phase 3.1, ported from the
// design's SavedSearchesPage (.agent/handoff-web/searches.jsx:67-124).
// Alerts cadence is PER SEARCH (user-directed 2026-07-15, supersedes Q7's
// global control): each card's Off/Daily/Instant Seg writes the saved
// search's own `cadence` field — the one the notification worker actually
// reads (the old global Seg wrote Firestore notifications.frequency, which
// the worker ignores). Instant stays Penthouse-gated client-side, with the
// server 403 as backstop.
export default function SearchesPage() {
  return (
    <RToastProvider>
      <SearchesPageInner />
    </RToastProvider>
  )
}

function SearchesPageInner() {
  const { user, loading: authLoading, signIn } = useAuth()
  const { t } = useI18n()
  const router = useRouter()
  const { show } = useRToast()

  const { setQuery, setFilters, setSort, setTravelTime, clearTags, addTag } = useSearchStore()
  const { canUse } = useSubscription()

  const [searches, setSearches] = React.useState<SavedSearch[]>([])
  const [counts, setCounts] = React.useState<Record<string, number>>({})
  const [isLoading, setIsLoading] = React.useState(true)
  // Error KEY, translated at render — keeping `t` out of fetchData's deps so
  // a language toggle doesn't refetch the list.
  const [error, setError] = React.useState<'unauthorized' | 'failed' | null>(null)
  const [showUpgradePrompt, setShowUpgradePrompt] = React.useState(false)

  const fetchData = React.useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const [savedSearches, notifCounts] = await Promise.all([
        api.getSavedSearches(),
        api.getNotificationCountsBySearch().catch(() => ({}) as Record<string, number>),
      ])
      setSearches(savedSearches)
      setCounts(notifCounts)
    } catch (err) {
      console.error('Error fetching saved searches:', err)
      setError(err instanceof ApiError && err.isUnauthorized ? 'unauthorized' : 'failed')
    } finally {
      setIsLoading(false)
    }
  }, [])

  React.useEffect(() => {
    if (user) fetchData()
  }, [user, fetchData])

  // ── Per-search cadence: Off = the same is_active pause as the kebab (the
  // stored cadence survives for resume); Daily/Instant write the search's own
  // `cadence` + re-activate explicitly (the backend never auto-activates —
  // that would bypass the subscription roostr limit). Optimistic with revert;
  // the server's Penthouse 403 on instant surfaces its own message.
  const handleCadenceChange = async (search: SavedSearch, value: SearchCadence) => {
    if (value === cadenceOf(search)) return
    if (value === 'instant' && !canUse('instant_notifications')) {
      setShowUpgradePrompt(true)
      return
    }
    const patch =
      value === 'off' ? { is_active: false } : { cadence: value, is_active: true }
    setSearches((prev) => prev.map((s) => (s.id === search.id ? { ...s, ...patch } : s)))
    try {
      const updated = await api.updateSavedSearch(search.id, patch)
      setSearches((prev) => prev.map((s) => (s.id === search.id ? { ...s, ...updated } : s)))
      show({
        msg:
          value === 'off'
            ? t('searches.pausedToast', 'Alerts paused')
            : value === 'daily'
              ? t('searches.alertsToastDaily', 'One digest every morning')
              : t('searches.alertsToastInstant', 'Alerts the moment a match lands'),
      })
    } catch (err) {
      console.error('Error updating search cadence:', err)
      setSearches((prev) => prev.map((s) => (s.id === search.id ? { ...s, ...search } : s)))
      show({
        msg:
          err instanceof ApiError && err.message
            ? err.message
            : t('searches.updateFailed', 'Failed to save the change'),
      })
    }
  }

  // ── Card click: load the saved criteria into /search. Mirrors
  // handleLoadSearch in components/search/saved-searches-panel.tsx (zustand
  // state) + the navigate-first path in components/auth/user-menu.tsx (the
  // search page's init effect picks the store state up after router.push).
  // Browsing does NOT mark the search seen (user-directed 2026-07-16) — the
  // "{n} new" counter is consumed only via the What's-new queue.
  const handleOpenInSearch = (search: SavedSearch) => {
    setQuery(search.original_query || '')
    setFilters((search.parsed_filters as RentalFilters) || ({ country: 'CZ' } as RentalFilters))
    setSort('newest')
    setTravelTime(search.travel_time || null)
    clearTags()

    const staticTagIds = new Set(STATIC_FILTER_TAGS.map((tag) => tag.id))
    if (search.tags && search.tags.length > 0) {
      search.tags.forEach((tagId) => {
        if (!staticTagIds.has(tagId)) addTag(tagId)
      })
    } else if (search.parsed_filters) {
      FILTER_TAGS.forEach((tag) => {
        if (staticTagIds.has(tag.id)) return
        const filterValue = search.parsed_filters?.[tag.value.field]
        if (filterValue !== undefined && filterValue === tag.value.value) addTag(tag.id)
      })
    }

    router.push('/search')
  }

  // ── Kebab: pause/resume alerts — optimistic is_active flip + toast.
  const handleToggleActive = async (search: SavedSearch) => {
    const nextActive = !search.is_active
    setSearches((prev) => prev.map((s) => (s.id === search.id ? { ...s, is_active: nextActive } : s)))
    try {
      const updated = await api.updateSavedSearch(search.id, { is_active: nextActive })
      setSearches((prev) => prev.map((s) => (s.id === search.id ? { ...s, ...updated } : s)))
      show({
        msg: nextActive
          ? t('searches.resumedToast', 'Alerts resumed')
          : t('searches.pausedToast', 'Alerts paused'),
      })
    } catch (err) {
      console.error('Error toggling saved search:', err)
      setSearches((prev) =>
        prev.map((s) => (s.id === search.id ? { ...s, is_active: search.is_active } : s))
      )
      show({
        msg:
          err instanceof ApiError && err.message
            ? err.message
            : t('searches.updateFailed', 'Failed to save the change'),
      })
    }
  }

  // ── Kebab: delete with Undo. Undo re-creates the search via
  // createSavedSearch from the deleted object (a new id is accepted).
  const recreateSearch = async (deleted: SavedSearch) => {
    try {
      const payload: SavedSearchCreate = {
        name: deleted.name,
        query: deleted.original_query || undefined,
        filters: (deleted.parsed_filters as RentalFilters) || undefined,
        tags: deleted.tags?.length ? deleted.tags : undefined,
        travel_time: deleted.travel_time || undefined,
        sort: deleted.sort,
        is_active: deleted.is_active,
        roostr_name: deleted.roostr_name || undefined,
        cadence: deleted.cadence,
      }
      const created = await api.createSavedSearch(payload)
      setSearches((prev) => [created, ...prev])
      window.dispatchEvent(new CustomEvent('saved-searches-changed'))
    } catch (err) {
      console.error('Error restoring saved search:', err)
      show({ msg: t('searches.restoreFailed', 'Could not restore the search') })
    }
  }

  const handleDelete = async (search: SavedSearch) => {
    setSearches((prev) => prev.filter((s) => s.id !== search.id))
    window.dispatchEvent(new CustomEvent('saved-searches-changed'))
    try {
      await api.deleteSavedSearch(search.id)
      show({
        msg: t('searches.deletedToast', 'Search deleted'),
        undo: () => {
          recreateSearch(search)
        },
      })
    } catch (err) {
      console.error('Error deleting saved search:', err)
      setSearches((prev) => [search, ...prev])
      show({ msg: t('searches.deleteFailed', 'Failed to delete the search') })
    }
  }

  // Review actions land in the "What's new" triage queue. A card's "{n} new"
  // pill scopes the queue to that search via ?search=<name> (the queue's
  // source filter keys on search_name); the header "Review all" spans every
  // search, so it goes unscoped.
  const handleReview = (search?: SavedSearch) =>
    router.push(search ? `/notifications?search=${encodeURIComponent(search.name)}` : '/notifications')

  const totalNew = searches.reduce((sum, s) => sum + (counts[s.id] || 0), 0)
  const instantLocked = !canUse('instant_notifications')

  // ── Signed out / loading states ─────────────────────────────────────────
  if (authLoading) {
    return (
      <Container>
        <PageSkeleton />
      </Container>
    )
  }

  if (!user) {
    return (
      <Container>
        <REmptyState
          className="pt-[96px]"
          icon={<BookmarkPlus size={30} />}
          title={t('searches.signInTitle', 'Sign in to keep searches')}
          text={t(
            'searches.signInText',
            'Saved searches watch the market and alert you the moment new matches appear.'
          )}
          action={
            <PillButton
              variant="primary"
              className="px-[18px] py-[10px] text-[13.5px]"
              onClick={() => signIn()}
            >
              <LogIn size={16} />
              {t('searches.signIn', 'Sign in')}
            </PillButton>
          }
        />
      </Container>
    )
  }

  return (
    <Container>
      {/* Header: title + subtitle left, global alerts Seg + review-all right */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-[30px] font-semibold tracking-[-0.5px] text-ink">
            {t('searches.title', 'Saved searches')}{' '}
            <span className="serif-accent text-primary">· {searches.length}</span>
          </h1>
          <p className="mt-1 text-ink-secondary">
            {t(
              'searches.subtitle',
              "Each search watches the market for you and files new matches into What's new."
            )}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-[14px]">
          {totalNew > 0 ? (
            <PillButton
              variant="primary"
              className="px-[18px] py-[10px] text-[13.5px]"
              onClick={() => handleReview()}
            >
              <Bell size={16} />
              {t('searches.reviewAll', 'Review all {n} new').replace('{n}', String(totalNew))}
            </PillButton>
          ) : null}
        </div>
      </div>

      {/* Body */}
      {isLoading ? (
        <CardsSkeleton />
      ) : error ? (
        <REmptyState
          className="pt-[64px]"
          icon={<X size={30} />}
          title={
            error === 'unauthorized'
              ? t('savedSearches.signInToView', 'Please sign in to view saved searches')
              : t('savedSearches.failedToLoad', 'Failed to load saved searches')
          }
          action={
            <PillButton className="px-[18px] py-[10px] text-[13.5px]" onClick={fetchData}>
              {t('savedSearches.tryAgain', 'Try Again')}
            </PillButton>
          }
        />
      ) : searches.length ? (
        <div className="mt-[22px] grid grid-cols-[repeat(auto-fill,minmax(min(320px,100%),1fr))] gap-[18px]">
          {searches.map((s) => (
            <SavedSearchCard
              key={s.id}
              search={s}
              newCount={counts[s.id] || 0}
              onOpen={() => handleOpenInSearch(s)}
              onReview={() => handleReview(s)}
              onToggleActive={() => handleToggleActive(s)}
              onDelete={() => handleDelete(s)}
              onCadenceChange={(value) => handleCadenceChange(s, value)}
              instantLocked={instantLocked}
            />
          ))}
        </div>
      ) : (
        <REmptyState
          icon={<BookmarkPlus size={30} />}
          title={t('searches.emptyTitle', 'No saved searches yet')}
          text={t(
            'searches.emptyText',
            'Filter the listings the way you like, then hit "Save search" — we\'ll watch the market for you.'
          )}
          action={
            <Link
              href="/search"
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full bg-primary px-[18px] py-[10px]',
                'text-[13.5px] font-semibold text-white shadow-e1'
              )}
            >
              {t('searches.startSearching', 'Start searching')}
            </Link>
          }
        />
      )}

      <UpgradePrompt open={showUpgradePrompt} onOpenChange={setShowUpgradePrompt} feature="instant_notifications" />
    </Container>
  )
}

// Page shell — maxWidth 1080, prototype paddings (searches.jsx:74).
function Container({ children }: { children: React.ReactNode }) {
  return <div className="mx-auto max-w-[1080px] px-4 pb-14 pt-5 md:px-8 md:pb-16 md:pt-7">{children}</div>
}

function CardsSkeleton() {
  return (
    <div className="mt-[22px] grid grid-cols-[repeat(auto-fill,minmax(min(320px,100%),1fr))] gap-[18px]">
      {[0, 1, 2].map((i) => (
        <Skeleton key={i} className="h-[150px] rounded-shape-lg bg-surface-high" />
      ))}
    </div>
  )
}

function PageSkeleton() {
  return (
    <div>
      <Skeleton className="h-9 w-72 rounded-full bg-surface-high" />
      <Skeleton className="mt-3 h-4 w-96 max-w-full rounded-full bg-surface-high" />
      <CardsSkeleton />
    </div>
  )
}
